import { getStorage, setStorage, removeStorage } from "./localStorage";

/**
 * Demo "membership" — the missing link between a demo-gate signup and
 * their ephemeral demo session.
 *
 * Under the gated-demo flow there are two distinct identities:
 *   • the real signup account `A` (has email + password, but no plan
 *     until they convert), and
 *   • the ephemeral demo user `D` that POST /demo/start mints from it
 *     (isDemoAccount + demoExpiresAt, a throwaway session whose own
 *     email is a `.invalid` placeholder).
 *
 * `D` lives only in the browser's localStorage and is wiped on logout —
 * you can't "log back in" as `D`. The only persistent identity is `A`.
 * So when a visitor logs out and signs back in as `A`, the login
 * response is just a planless account, and the app used to route them
 * straight to /pricing with no memory that they were ever mid-demo.
 *
 * This record closes that gap. We stamp it when a demo launches —
 * keyed to the parent signup email — and it survives logout (it's on
 * the PRESERVE_ON_LOGOUT allowlist in localStorage.js). On the next
 * login we use it to:
 *   1. route the returning planless signup back into their demo
 *      instead of /pricing (see getReturningDemoTarget), and
 *   2. decide whether that demo is still live or has expired (see
 *      isDemoMembershipExpired) so DemoBanner can throw up the
 *      "demo's over, pick a plan" gate.
 *
 * `demoExpiresAt` is the authoritative expiry captured at FIRST launch
 * and is never extended on relaunch — the gate grants one window, and a
 * re-login that re-seeds the sample data shouldn't reset the clock.
 *
 * It's intentionally low-stakes (client-side, editable). The real gate
 * is the backend: an expired/planless account still can't touch paid
 * features or a real CRM without a subscription. This record only
 * controls which friendly screen we show.
 */

const KEY = "__demo_membership__";

function normEmail(email) {
  return String(email || "").trim().toLowerCase();
}

export function getDemoMembership() {
  try {
    const m = getStorage(KEY, "object");
    if (!m || !m.email || !m.vertical) return null;
    return m;
  } catch {
    return null;
  }
}

/**
 * Stamp (or refresh) the demo membership for the parent signup account.
 * Called from DemoLauncher right after a demo session is established.
 *
 * Preserves the ORIGINAL expiry: if a membership already exists for the
 * same email, we keep the earlier `demoExpiresAt` so relaunching the
 * tour never buys more time.
 */
export function saveDemoMembership({ email, vertical, tier, demoExpiresAt }) {
  const e = normEmail(email);
  if (!e || !vertical) return;

  let expiresAt = demoExpiresAt || null;
  const existing = getDemoMembership();
  if (existing && existing.email === e && existing.demoExpiresAt) {
    // Keep whichever window ends sooner — the original one wins so a
    // re-seed on re-login can't extend an expired demo.
    if (!expiresAt || new Date(existing.demoExpiresAt) < new Date(expiresAt)) {
      expiresAt = existing.demoExpiresAt;
    }
  }

  try {
    setStorage(KEY, { email: e, vertical, tier: tier || "pro", demoExpiresAt: expiresAt }, "object");
  } catch {
    /* localStorage unavailable — non-fatal, routing just falls back to /pricing */
  }
}

export function clearDemoMembership() {
  try {
    removeStorage(KEY);
  } catch {
    /* ignore */
  }
}

/**
 * True when the stored demo window has elapsed. Used by DemoBanner to
 * decide whether to render the blocking "demo expired" gate over the
 * dashboard, independent of whatever (possibly re-seeded) expiry the
 * live demo session carries.
 */
export function isDemoMembershipExpired() {
  const m = getDemoMembership();
  if (!m || !m.demoExpiresAt) return false;
  return new Date(m.demoExpiresAt).getTime() <= Date.now();
}

/**
 * True when a membership exists AND belongs to `email`.
 *
 * Cross-user safety: the record is keyed to the parent signup email and
 * survives logout (it's on PRESERVE_ON_LOGOUT), so it lives per-DEVICE,
 * not per-user. A *different* account signing up on the same device must
 * not inherit the previous visitor's (possibly expired) membership —
 * otherwise a brand-new user gets the "your demo has ended" gate for a
 * tour they never took. Callers that know the current account's email
 * gate on this instead of the raw getDemoMembership()/isDemoMembership-
 * Expired() so the stale record only ever affects its true owner.
 */
export function demoMembershipBelongsTo(email) {
  const e = normEmail(email);
  if (!e) return false;
  const m = getDemoMembership();
  return !!m && m.email === e;
}

/**
 * Where should a freshly-authenticated user land, if they're a returning
 * demo-gate signup? Returns a `/demo/:vertical/:tier` path to send them
 * back into their tour, or null to let normal role/plan routing run.
 *
 * Returns null when:
 *   • there's no membership on this device,
 *   • the membership belongs to a different email than the one signing
 *     in (don't drop someone else into a stale tour), or
 *   • the account already holds a real plan — they've converted, so
 *     normal routing should take them to their CRM, not another demo.
 *
 * Note: expired memberships still return a target. We WANT the demo to
 * "pop up" so the expired gate can ask them to pick a plan; the gate
 * itself lives in DemoBanner, driven by isDemoMembershipExpired().
 */
export function getReturningDemoTarget(user) {
  const m = getDemoMembership();
  if (!m) return null;

  const email = normEmail(user?.email);
  if (!email || email !== m.email) return null;

  // Already on a real plan → converted; let them into their CRM.
  if (user?.plan?.category || user?.planCategory) return null;

  return `/demo/${m.vertical}/${m.tier || "pro"}`;
}
